/**
 * Abonos a Capital — migración web de ares8060 (escritorio).
 *
 * Aplica un abono extraordinario al capital de un préstamo activo: rebaja el
 * capital pendiente, recalcula el interés y la cuota con la misma tasa y
 * cantidad de cuotas restantes, y regenera la amortización (todo en el
 * backend /api/abonocapital_guardar). Si el préstamo tiene pagos parciales
 * de cuota el abono queda bloqueado (igual que el escritorio). Mantiene un
 * historial de abonos y permite eliminar SOLO el más reciente, lo que
 * restaura la amortización previa (/api/abonocapital_eliminar).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { redondearCuota } from "lib/calculocuotas";
import { imprimirReciboPdf } from "lib/recibopdf";
import { usePortalQr, portalQrActual } from "lib/portalqr";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function AbonosCapital({ noprestInicial, enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  usePortalQr();

  const [prestamos, setPrestamos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [prestamo, setPrestamo] = useState(null); // opción del autocomplete
  const [datos, setDatos] = useState(null); // detalle de abonocapital_datos
  const [historial, setHistorial] = useState([]);
  const [buscando, setBuscando] = useState(false);

  const [valor, setValor] = useState("");
  const [tipodepago, setTipodepago] = useState("Contado");
  const [referencia, setReferencia] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [eliminarSel, setEliminarSel] = useState(null); // abono a eliminar (fila 0)
  const [eliminando, setEliminando] = useState(false);

  const cargarPrestamos = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/refinanciamiento_prestamos", {});
      if (ok) setPrestamos(data.prestamos || []);
      else
        showAlert({ message: data.Error || "No se pudieron cargar los préstamos", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPrestamos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async (np) => {
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/abonocapital_datos", { noprest: np });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo recuperar el préstamo", color: "error" });
        setPrestamo(null);
        return;
      }
      setDatos(data.prestamo);
      setHistorial(data.historial || []);
    } finally {
      setBuscando(false);
    }
  };

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !prestamos.length) return;
    const opcion = prestamos.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) elegirPrestamo(opcion);
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestamos]);

  const elegirPrestamo = async (p) => {
    setPrestamo(p);
    setDatos(null);
    setHistorial([]);
    setValor("");
    setTipodepago("Contado");
    setReferencia("");
    if (!p) return;
    await cargarDatos(p.noprest);
  };

  const limpiar = () => {
    setPrestamo(null);
    setDatos(null);
    setHistorial([]);
    setValor("");
    setTipodepago("Contado");
    setReferencia("");
    setConfirmOpen(false);
    setEliminarSel(null);
  };

  const bloqueado = !!datos?.bloqueadoporpagosparciales;

  // Vista previa local del recálculo (misma fórmula flat-rate del backend):
  // capitalnuevo = capital − abono; interesnuevo = capitalnuevo * tasa * cuotas;
  // cuotanueva = (capitalnuevo + interesnuevo) / cuotas.
  const preview = useMemo(() => {
    if (!datos) return null;
    const abono = toNum(valor);
    const capital = toNum(datos.capitalpendiente);
    const cuotas = toNum(datos.cuotas);
    const tasa = toNum(datos.tasa);
    if (abono <= 0 || cuotas <= 0) return null;
    const capitalnuevo = capital - abono;
    if (capitalnuevo < 0) return null;
    let interesnuevo = capitalnuevo * tasa * cuotas;
    let cuotanueva = (capitalnuevo + interesnuevo) / cuotas;
    // Redondeo de cuotas (mismo criterio del backend): la diferencia va al
    // interés, salvo que supere el interés pendiente (abono muy pequeño).
    if (datos.redondeocuota === "S" && capitalnuevo > 0) {
      const cuotared = redondearCuota(cuotanueva);
      const interesred = (cuotared - capitalnuevo / cuotas) * cuotas;
      if (interesred <= toNum(datos.interespendiente) + 0.01) {
        cuotanueva = cuotared;
        interesnuevo = interesred;
      }
    }
    return { capitalnuevo, interesnuevo, cuotanueva };
  }, [datos, valor]);

  const validar = () => {
    if (!datos) {
      showAlert({ message: "Debe seleccionar el préstamo", color: "warning" });
      return false;
    }
    if (bloqueado) {
      showAlert({
        message: "El préstamo tiene pagos parciales de cuota; no se puede abonar a capital",
        color: "warning",
      });
      return false;
    }
    if (toNum(valor) <= 0) {
      showAlert({ message: "Indique el valor del abono", color: "warning" });
      return false;
    }
    // Igual al capital = saldar el préstamo (el backend cierra cuotas y préstamo).
    if (toNum(valor) > toNum(datos.capitalpendiente) + 0.01) {
      showAlert({
        message: "El abono no puede ser mayor al capital pendiente",
        color: "warning",
      });
      return false;
    }
    if (tipodepago !== "Contado" && !referencia.trim()) {
      showAlert({ message: "Indique la referencia del pago", color: "warning" });
      return false;
    }
    return true;
  };

  // Comprobante en PDF con el formato de bankivo (lib/recibopdf). También lo
  // usa la reimpresión: info.fecha trae la fecha original del abono, y los
  // totales en null (abono viejo sin snapshot) no se imprimen.
  const imprimirComprobante = (info) => {
    const now = new Date();
    imprimirReciboPdf({
      titulo: "Abono a Capital",
      compania: "",
      campos: [
        ["Abono #", info.idabono || ""],
        ["Prestamo", info.noprest],
        ["Cliente", info.cliente],
        [
          "Fecha",
          info.fecha || `${now.toLocaleDateString("es-DO")} ${now.toLocaleTimeString("es-DO")}`,
        ],
        ["Referencia", info.referencia || ""],
      ],
      totales: [
        { etiqueta: "Valor abonado", valor: info.valor, destacado: true },
        { etiqueta: "Interes descontado", valor: info.interesdescontado },
        { etiqueta: "Capital nuevo", valor: info.capitalnuevo },
        { etiqueta: "Cuota nueva", valor: info.cuotanueva },
      ].filter((t) => t.valor != null),
      firma: "Firma",
      qr: portalQrActual(),
    });
  };

  // Reimpresión desde el historial: el backend reconstruye capital y cuota
  // nuevos desde el snapshot del abono (/api/abonocapital_recibo).
  const reimprimir = async (h) => {
    const { ok, data } = await post("/api/abonocapital_recibo", { idabono: h.idabono });
    if (!ok || !data.recibo) {
      showAlert({ message: data.Error || "No se pudo recuperar el recibo", color: "error" });
      return;
    }
    imprimirComprobante(data.recibo);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/abonocapital_guardar", {
        noprest: datos.noprest,
        valor: toNum(valor),
        tipodepago,
        referencia: referencia.trim(),
      });
      if (ok) {
        showAlert({
          message: data.saldado
            ? `${data.ok || "Abono registrado"} — El préstamo quedó saldado`
            : `${data.ok || "Abono registrado"} — Cuota nueva RD$ ${fmtMoney(data.cuotanueva)}`,
          color: "success",
        });
        imprimirComprobante({
          idabono: data.idabono,
          noprest: datos.noprest,
          cliente: datos.nombres,
          referencia: referencia.trim(),
          valor: toNum(valor),
          interesdescontado: data.interesdescontado,
          capitalnuevo: data.capitalnuevo,
          cuotanueva: data.cuotanueva,
        });
        setConfirmOpen(false);
        setValor("");
        setReferencia("");
        setTipodepago("Contado");
        if (data.saldado) {
          // Saldado = cancelado: ya no sale en la lista de préstamos activos.
          setPrestamo(null);
          setDatos(null);
          setHistorial([]);
          await cargarPrestamos();
        } else {
          await cargarDatos(datos.noprest);
        }
      } else {
        showAlert({ message: data.Error || "No se pudo registrar el abono", color: "error" });
        setConfirmOpen(false);
      }
    } finally {
      setGuardando(false);
    }
  };

  const eliminarAbono = async () => {
    if (!eliminarSel) return;
    setEliminando(true);
    try {
      const { ok, data } = await post("/api/abonocapital_eliminar", {
        idabono: eliminarSel.idabono,
      });
      if (ok) {
        showAlert({ message: data.ok || "Abono eliminado", color: "success" });
        setEliminarSel(null);
        if (datos) await cargarDatos(datos.noprest);
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar el abono", color: "error" });
      }
    } finally {
      setEliminando(false);
    }
  };

  const chipInfo = {
    bgcolor: FP.color.surfaceAlt,
    color: FP.color.textSecondary,
    fontWeight: 700,
  };

  const thSx = {
    fontWeight: 800,
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: FP.color.textMuted,
    borderBottom: `1px solid ${FP.color.border}`,
    p: 1.2,
    textAlign: "left",
  };
  const tdSx = {
    fontSize: "0.85rem",
    color: FP.color.textPrimary,
    borderBottom: `1px solid ${FP.color.border}`,
    p: 1.2,
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title="Abonos a Capital"
        subtitle="Rebaja el capital de un préstamo activo y recalcula interés y cuota (ares8060)"
        cta={
          <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
            Nuevo
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Préstamo */}
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <SectionTitle icon={RequestQuoteOutlinedIcon} color={FP.color.accentBlue}>
            Préstamo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                loading={cargando || buscando}
                value={prestamo}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => elegirPrestamo(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest} — {o.nombres}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.cedula, o.chasis, o.sucursal].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo"
                    helperText="Busca por número, nombre, cédula o chasis"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {datos && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={datos.nombres} sx={chipInfo} />
                  <Chip label={`Cédula ${datos.cedula || "—"}`} sx={chipInfo} />
                  <Chip label={`${datos.cuotas} cuotas pendientes`} sx={chipInfo} />
                  <Chip
                    label={`Capital pendiente: RD$ ${fmtMoney(datos.capitalpendiente)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                  <Chip
                    label={`Interés pendiente: RD$ ${fmtMoney(datos.interespendiente)}`}
                    sx={{
                      bgcolor: FP.color.accentBlueSoft,
                      color: FP.color.accentBlue,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Grid>
            )}
            {bloqueado && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    border: `1px solid ${FP.color.warning}55`,
                    bgcolor: `${FP.color.warning}0c`,
                    borderRadius: 2,
                    p: 1.6,
                  }}
                >
                  <WarningAmberOutlinedIcon sx={{ color: FP.color.warning }} />
                  <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
                    El préstamo tiene <b>pagos parciales de cuota</b>: no se puede aplicar un abono
                    a capital hasta que la cuota en curso quede completa.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Abono */}
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <SectionTitle icon={SavingsOutlinedIcon} color={FP.color.success}>
            Abono
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Valor del abono"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                disabled={!datos || bloqueado}
                sx={fpInputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Tipo de pago"
                value={tipodepago}
                onChange={(e) => setTipodepago(e.target.value)}
                disabled={!datos || bloqueado}
                sx={fpInputSx}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Transferencias">Transferencias</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Referencia"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                disabled={!datos || bloqueado}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8} sx={{ display: "flex", alignItems: "center" }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Capital nuevo: RD$ ${preview ? fmtMoney(preview.capitalnuevo) : "0.00"}`}
                  sx={chipInfo}
                />
                <Chip
                  label={`Interés nuevo: RD$ ${preview ? fmtMoney(preview.interesnuevo) : "0.00"}`}
                  sx={chipInfo}
                />
                <Chip
                  label={`Cuota nueva: RD$ ${preview ? fmtMoney(preview.cuotanueva) : "0.00"}`}
                  sx={{
                    bgcolor: `${FP.color.success}14`,
                    color: FP.color.success,
                    fontWeight: 700,
                  }}
                />
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Button
                startIcon={
                  guardando ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PaymentsOutlinedIcon />
                  )
                }
                onClick={() => {
                  if (validar()) setConfirmOpen(true);
                }}
                disabled={guardando || !datos || bloqueado}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Abonar a capital
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Historial de abonos */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.8,
              borderBottom: `1px solid ${FP.color.border}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <HistoryOutlinedIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
              Historial de abonos
            </Typography>
          </Box>
          <Box sx={{ overflowX: "auto", bgcolor: "#fff" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={thSx}>
                    Fecha
                  </Box>
                  <Box component="th" sx={{ ...thSx, textAlign: "right" }}>
                    Valor
                  </Box>
                  <Box component="th" sx={{ ...thSx, textAlign: "right" }}>
                    Interés descontado
                  </Box>
                  <Box component="th" sx={thSx}>
                    Banco
                  </Box>
                  <Box component="th" sx={thSx}>
                    Referencia
                  </Box>
                  <Box component="th" sx={thSx}>
                    Cuotas
                  </Box>
                  <Box component="th" sx={thSx} />
                </Box>
              </Box>
              <Box component="tbody">
                {historial.map((h, i) => (
                  <Box component="tr" key={h.idabono}>
                    <Box component="td" sx={tdSx}>
                      {h.fecha}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, textAlign: "right", fontWeight: 700 }}>
                      RD$ {fmtMoney(h.valor)}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
                      RD$ {fmtMoney(h.interesdescontado)}
                    </Box>
                    <Box component="td" sx={tdSx}>
                      {h.banco || "—"}
                    </Box>
                    <Box component="td" sx={tdSx}>
                      {h.referencia || "—"}
                    </Box>
                    <Box component="td" sx={tdSx}>
                      {h.cuotadesde} — {h.cuotahasta}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<PrintOutlinedIcon />}
                          onClick={() => reimprimir(h)}
                          sx={btnOutline(FP.color.navy)}
                        >
                          Recibo
                        </Button>
                        {i === 0 && (
                          <Button
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setEliminarSel(h)}
                            sx={btnOutline(FP.color.error)}
                          >
                            Eliminar
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                ))}
                {!historial.length && (
                  <Box component="tr">
                    <Box
                      component="td"
                      colSpan={7}
                      sx={{ ...tdSx, textAlign: "center", color: FP.color.textMuted, py: 4 }}
                    >
                      {datos
                        ? "El préstamo no tiene abonos a capital registrados"
                        : "Seleccione un préstamo para ver sus abonos"}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Confirmación del abono */}
      <Dialog
        open={confirmOpen}
        onClose={() => !guardando && setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <WarningAmberOutlinedIcon sx={{ color: FP.color.warning }} />
          Confirmar abono a capital
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 1 }}>
            Se aplicará un abono de <b>RD$ {fmtMoney(valor)}</b> al préstamo{" "}
            <b>#{datos?.noprest}</b> de <b>{datos?.nombres}</b>. La amortización se recalculará:
          </Typography>
          {preview && (
            <Stack spacing={0.4}>
              <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
                Capital nuevo: <b>RD$ {fmtMoney(preview.capitalnuevo)}</b>
              </Typography>
              <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
                Interés nuevo: <b>RD$ {fmtMoney(preview.interesnuevo)}</b>
              </Typography>
              <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
                Cuota nueva: <b>RD$ {fmtMoney(preview.cuotanueva)}</b>
              </Typography>
            </Stack>
          )}
          <Divider sx={{ my: 1.4 }} />
          <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
            ¿Desea continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={guardando} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              guardando ? <CircularProgress size={16} color="inherit" /> : <PaymentsOutlinedIcon />
            }
            onClick={guardar}
            disabled={guardando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Sí, abonar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación (solo el abono más reciente) */}
      <Dialog
        open={!!eliminarSel}
        onClose={() => !eliminando && setEliminarSel(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <WarningAmberOutlinedIcon sx={{ color: FP.color.error }} />
          Eliminar abono a capital
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary }}>
            Se eliminará el abono de <b>RD$ {fmtMoney(eliminarSel?.valor)}</b> del{" "}
            <b>{eliminarSel?.fecha}</b> y se <b>restaurará la amortización anterior</b> del préstamo
            (capital, interés y cuotas vuelven a como estaban antes del abono). ¿Desea continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEliminarSel(null)} disabled={eliminando} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminarAbono}
            disabled={eliminando}
            sx={btnSolid(FP.color.error, "#B71C1C")}
          >
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
